import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { formatInstant, formatReportCell, resolveFormattingLocale } from '@eis/core/reporting';
import {
  EmptyState,
  ErrorState,
  PageHeader,
  PageLayout,
  RequirePermission,
  cardSurfaceSx,
} from '@eis/patterns';
import { reportingPermissions as p } from '../../../permissions';
import {
  isConflictError,
  useDatasets,
  useRefreshCatalog,
  useUpdateWorkspaceSettings,
  useWorkspaceSettings,
} from '../api/settings';
import {
  DATETIME_PATTERNS,
  DATE_PATTERNS,
  REUSE_MINUTES,
  settingsDocumentOf,
  type SettingsDocument,
} from '../model/settings';

/**
 * Einstellungen (SDD-024 F-1, Q-4, W-2; mockup board 9): the workspace's default formats with a
 * live sample each, Athena's result-reuse window, the fixed limits, and the warehouse connection —
 * read-only, it comes from the placement registry. One document per tenant, saved with If-Match.
 * `reporting.settings.manage` only (the admin's).
 */
export function SettingsPage(): ReactNode {
  const { t } = useTranslation('reporting');
  return (
    <RequirePermission
      anyOf={[p.settings.manage]}
      fallback={
        <PageLayout header={<PageHeader title={t('settings.title')} />}>
          <EmptyState title={t('noAccess.title')} description={t('noAccess.settings')} />
        </PageLayout>
      }
    >
      <SettingsForm />
    </RequirePermission>
  );
}

function SettingsForm(): ReactNode {
  const { t } = useTranslation('reporting');
  const settings = useWorkspaceSettings();
  if (settings.isError) {
    return (
      <PageLayout header={<PageHeader title={t('settings.title')} />}>
        <ErrorState description={t('settings.loadError')} onRetry={() => void settings.refetch()} />
      </PageLayout>
    );
  }
  if (settings.data === undefined) {
    return (
      <PageLayout header={<PageHeader title={t('settings.title')} />}>
        <Skeleton variant="rounded" height={360} />
      </PageLayout>
    );
  }
  // A saved or re-read document (a new version) starts the form again from it.
  return <SettingsEditor key={settings.data.version ?? '0'} />;
}

function SettingsEditor(): ReactNode {
  const { t, i18n } = useTranslation('reporting');
  const locale = resolveFormattingLocale(i18n.language);
  const settings = useWorkspaceSettings();
  const update = useUpdateWorkspaceSettings();
  const datasets = useDatasets();
  const refresh = useRefreshCatalog();
  const [doc, setDoc] = useState<SettingsDocument>(() =>
    settingsDocumentOf(settings.data?.document),
  );
  const [saved, setSaved] = useState(false);

  const f = doc.formats;
  const setFormats = (patch: Partial<SettingsDocument['formats']>): void => {
    setSaved(false);
    setDoc({ ...doc, formats: { ...f, ...patch } });
  };
  const sample = (
    raw: string,
    type: 'DATE' | 'DATETIME' | 'NUMBER' | 'BOOLEAN',
    kind?: 'CURRENCY' | 'PERCENT',
  ): string =>
    formatReportCell(raw, {
      type,
      ...(kind === undefined ? {} : { format: { kind } }),
      settings: doc,
      locale: doc.locale,
    });
  const connection = settings.data?.connection;
  const location = [
    connection?.catalog,
    connection?.database,
    connection?.schema,
    connection?.workgroup,
  ]
    .filter((x) => x !== undefined && x !== '')
    .join(' · ');
  const conflict = update.error !== null && isConflictError(update.error);

  const header = (
    <PageHeader
      title={t('settings.title')}
      actions={
        <Button
          size="small"
          variant="contained"
          disabled={update.isPending}
          onClick={() => {
            update.mutate(
              {
                document: doc as unknown as Record<string, unknown>,
                etag: settings.data?.etag,
                version: settings.data?.version,
              },
              {
                onSuccess: () => {
                  setSaved(true);
                },
              },
            );
          }}
        >
          {t('settings.save')}
        </Button>
      }
    />
  );

  return (
    <PageLayout header={header}>
      <Stack spacing={2}>
        {saved && <Alert severity="success">{t('settings.saved')}</Alert>}
        {conflict && <Alert severity="warning">{t('settings.conflict')}</Alert>}
        <Paper variant="outlined" sx={(theme) => ({ p: 2, ...cardSurfaceSx(theme) })}>
          <Typography variant="subtitle2" component="h2" sx={{ mb: 1.5 }}>
            {t('settings.formats')}
          </Typography>
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <TextField
              select
              size="small"
              label={t('settings.date')}
              value={f.date.pattern}
              helperText={`→ ${sample('2026-10-31', 'DATE')}`}
              onChange={(e) => {
                setFormats({ date: { pattern: e.target.value } });
              }}
            >
              {DATE_PATTERNS.map((x) => (
                <MenuItem key={x} value={x}>
                  {x}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('settings.datetime')}
              value={f.datetime.pattern}
              helperText={`→ ${sample('2026-10-31 01:10:00', 'DATETIME')}`}
              onChange={(e) => {
                setFormats({ datetime: { pattern: e.target.value } });
              }}
            >
              {DATETIME_PATTERNS.map((x) => (
                <MenuItem key={x} value={x}>
                  {x}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('settings.timeZone')}
              value={doc.timeZone}
              onChange={(e) => {
                setSaved(false);
                setDoc({ ...doc, timeZone: e.target.value });
              }}
            />
            <TextField
              select
              size="small"
              label={t('settings.locale')}
              value={doc.locale}
              onChange={(e) => {
                setSaved(false);
                setDoc({ ...doc, locale: e.target.value as SettingsDocument['locale'] });
              }}
            >
              <MenuItem value="de-DE">de-DE</MenuItem>
              <MenuItem value="en-GB">en-GB</MenuItem>
            </TextField>
            <TextField
              size="small"
              type="number"
              label={t('settings.numberDecimals')}
              value={f.number.decimals}
              helperText={`→ ${sample('1234.5678', 'NUMBER')}`}
              slotProps={{ htmlInput: { min: 0, max: 6 } }}
              onChange={(e) => {
                setFormats({ number: { ...f.number, decimals: Number(e.target.value) } });
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={f.number.grouping}
                  onChange={(e) => {
                    setFormats({ number: { ...f.number, grouping: e.target.checked } });
                  }}
                />
              }
              label={t('settings.numberGrouping')}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label={t('settings.currency')}
                value={f.currency.code}
                slotProps={{ htmlInput: { maxLength: 3 } }}
                onChange={(e) => {
                  setFormats({ currency: { ...f.currency, code: e.target.value.toUpperCase() } });
                }}
              />
              <TextField
                size="small"
                type="number"
                label={t('settings.currencyDecimals')}
                value={f.currency.decimals}
                helperText={`→ ${sample('1234.56', 'NUMBER', 'CURRENCY')}`}
                slotProps={{ htmlInput: { min: 0, max: 4 } }}
                onChange={(e) => {
                  setFormats({ currency: { ...f.currency, decimals: Number(e.target.value) } });
                }}
              />
            </Stack>
            <TextField
              size="small"
              type="number"
              label={t('settings.percentDecimals')}
              value={f.percent.decimals}
              helperText={`→ ${sample('0.123', 'NUMBER', 'PERCENT')}`}
              slotProps={{ htmlInput: { min: 0, max: 4 } }}
              onChange={(e) => {
                setFormats({ percent: { decimals: Number(e.target.value) } });
              }}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label={t('settings.trueLabel')}
                value={f.boolean.trueLabel}
                onChange={(e) => {
                  setFormats({ boolean: { ...f.boolean, trueLabel: e.target.value } });
                }}
              />
              <TextField
                size="small"
                label={t('settings.falseLabel')}
                value={f.boolean.falseLabel}
                onChange={(e) => {
                  setFormats({ boolean: { ...f.boolean, falseLabel: e.target.value } });
                }}
              />
            </Stack>
            <TextField
              size="small"
              label={t('settings.empty')}
              value={f.empty}
              slotProps={{ htmlInput: { maxLength: 10 } }}
              onChange={(e) => {
                setFormats({ empty: e.target.value });
              }}
            />
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={(theme) => ({ p: 2, ...cardSurfaceSx(theme) })}>
          <Typography variant="subtitle2" component="h2" sx={{ mb: 1.5 }}>
            {t('settings.queries')}
          </Typography>
          <Stack spacing={1.5} sx={{ maxWidth: 560 }}>
            <TextField
              select
              size="small"
              label={t('settings.reuse')}
              value={doc.resultReuseMinutes}
              onChange={(e) => {
                setSaved(false);
                setDoc({ ...doc, resultReuseMinutes: Number(e.target.value) });
              }}
            >
              {REUSE_MINUTES.map((m) => (
                <MenuItem key={m} value={m}>
                  {m === 0 ? t('settings.reuseOff') : t('settings.reuseMinutes', { count: m })}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2">{t('settings.maxDuration')}</Typography>
            <Typography variant="body2">{t('settings.maxCells')}</Typography>
            <Typography variant="body2">{t('settings.exportLimit')}</Typography>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={(theme) => ({ p: 2, ...cardSurfaceSx(theme) })}>
          <Typography variant="subtitle2" component="h2" sx={{ mb: 1.5 }}>
            {t('settings.catalog')}
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">
              {t('settings.connection', {
                provider: connection?.provider ?? '—',
                location: location === '' ? '—' : location,
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('settings.connectionReadOnly')}
            </Typography>
            {datasets.data?.fetchedAt !== undefined && (
              <Typography variant="body2">
                {t('settings.lastRefresh', { at: formatInstant(datasets.data.fetchedAt, locale) })}
              </Typography>
            )}
            <Button
              size="small"
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
              disabled={refresh.isPending}
              onClick={() => {
                refresh.mutate({});
              }}
            >
              {t('settings.refreshNow')}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </PageLayout>
  );
}
